import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Users as UsersIcon,
  Building,
  Shield,
  X,
  Eye,
  Check,
  AlertCircle,
} from "lucide-react";
import { adminService } from "../api/adminService";
import { useNotification } from "../contexts/NotificationContext";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create', 'edit', 'view'
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
    tenant_id: "",
    is_system_admin: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    loadUsers();
    loadTenants();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminService.users.list({ limit: 1000 });

      // Try different response structures
      let usersData = [];
      if (response?.data?.data) {
        usersData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        usersData = response.data;
      } else if (response?.data?.users) {
        usersData = response.data.users;
      }

      setUsers(usersData);
    } catch (error) {
      console.error("Error loading users:", error);
      showError(
        "Gagal memuat data users: " +
          (error.response?.data?.message || error.message),
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      setLoadingTenants(true);
      const response = await adminService.tenants.list({ limit: 1000 });

      let tenantsData = [];
      if (response?.data?.data) {
        tenantsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        tenantsData = response.data;
      } else if (response?.data?.tenants) {
        tenantsData = response.data.tenants;
      }

      setTenants(tenantsData);
    } catch (error) {
      console.error("Error loading tenants:", error);
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  const openCreateModal = () => {
    setModalMode("create");
    setSelectedUser(null);
    setFormData({
      email: "",
      password: "",
      full_name: "",
      phone: "",
      tenant_id: "",
      is_system_admin: false,
    });
    setFormErrors({});
    setShowModal(true);
    // Reload tenants when opening create modal to ensure fresh data
    loadTenants();
  };

  const openEditModal = (user) => {
    setModalMode("edit");
    setSelectedUser(user);
    setFormData({
      email: user.email || "",
      password: "", // Don't show existing password
      full_name: user.full_name || "",
      phone: user.phone || "",
      tenant_id: "", // Will be handled separately
      is_system_admin: user.is_system_admin || false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const openViewModal = (user) => {
    setModalMode("view");
    setSelectedUser(user);
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email format is invalid";
    }

    if (modalMode === "create" && !formData.password) {
      errors.password = "Password is required";
    } else if (formData.password && formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    if (!formData.full_name) {
      errors.full_name = "Full name is required";
    }

    if (!formData.tenant_id && !formData.is_system_admin) {
      errors.tenant_id = "Tenant is required for non-admin users";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const submitData = {
        ...formData,
        tenant_id: formData.is_system_admin ? undefined : formData.tenant_id,
      };

      if (modalMode === "edit") {
        // Don't include password if it's empty
        if (!submitData.password) {
          delete submitData.password;
        }
        delete submitData.tenant_id; // Handle tenant assignment separately if needed

        await adminService.users.update(selectedUser.id, submitData);
        showSuccess("User berhasil diperbarui");
      } else {
        await adminService.users.create(submitData);
        showSuccess("User berhasil dibuat");
      }

      await loadUsers();
      setShowModal(false);
    } catch (error) {
      console.error("Error saving user:", error);
      showError(error.response?.data?.message || "Gagal menyimpan user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus user ini?")) {
      return;
    }

    try {
      await adminService.users.delete(userId);
      showSuccess("User berhasil dihapus");
      await loadUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      showError("Gagal menghapus user");
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesEmail = user.email?.toLowerCase().includes(searchLower);
    const matchesName = user.full_name?.toLowerCase().includes(searchLower);
    const matchesPhone = user.phone?.toLowerCase().includes(searchLower);
    
    const roleStr = user.is_system_admin ? "system admin" : "user";
    const matchesRole = roleStr.includes(searchLower);
    
    const matchesTenant = user.tenants?.some((tenant) =>
      tenant.name?.toLowerCase().includes(searchLower)
    );

    return matchesEmail || matchesName || matchesPhone || matchesRole || matchesTenant;
  });

  const totalEntries = filteredUsers.length;
  const maxPages = Math.ceil(totalEntries / itemsPerPage);
  const startEntry = totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Debug logging
  console.log("UserManagement render state:", {
    loading,
    usersCount: users.length,
    filteredUsersCount: filteredUsers.length,
    searchTerm,
    users: users.slice(0, 2), // Show first 2 users for debug
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            User Management
          </h1>
          <p className="text-gray-600">Manage system users and their access</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openCreateModal}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
            <Plus className="w-4 h-4" />
            Add User
          </button>
          <button
            onClick={loadUsers}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            disabled={loading}>
            {loading ? "Loading..." : "Reload Users"}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search users by email, name, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full max-w-md border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="text-gray-500">Loading users...</div>
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="text-gray-500">No users found</div>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <UsersIcon className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                          {user.phone && (
                            <div className="text-xs text-gray-400">
                              {user.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.is_system_admin ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <Shield className="w-3 h-3 mr-1" />
                          System Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <UsersIcon className="w-3 h-3 mr-1" />
                          User
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {user.tenants?.length > 0 ? (
                          <div className="space-y-1">
                            {user.tenants.slice(0, 2).map((tenant, index) => (
                              <div
                                key={tenant.id}
                                className="flex items-center gap-2">
                                <Building className="w-3 h-3 text-gray-400" />
                                <span className="text-xs">{tenant.name}</span>
                                <span
                                  className={`px-1.5 py-0.5 text-xs rounded-full ${
                                    tenant.status === "ACTIVE"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-gray-100 text-gray-700"
                                  }`}>
                                  {tenant.status}
                                </span>
                              </div>
                            ))}
                            {user.tenants.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{user.tenants.length - 2} more
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No tenants
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openViewModal(user)}
                          className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                          title="Detail">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="w-8 h-8 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center transition-colors"
                          title="Edit">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="w-8 h-8 bg-rose-500 text-white rounded-lg hover:bg-rose-600 flex items-center justify-center transition-colors"
                          title="Hapus">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {filteredUsers.length > 0 && maxPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-6 py-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">
            Menampilkan <span className="font-semibold text-gray-700">{startEntry}</span> -{" "}
            <span className="font-semibold text-gray-700">{endEntry}</span> dari{" "}
            <span className="font-semibold text-gray-700">{totalEntries}</span> user
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Sebelumnya
            </button>
            
            {Array.from({ length: maxPages }).map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 text-xs font-medium rounded-lg transition-colors ${
                    currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "border border-gray-200 hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, maxPages))}
              disabled={currentPage === maxPages}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Selanjutnya
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop-enter">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto modal-panel-enter">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalMode === "create" && "Create User"}
                {modalMode === "edit" && "Edit User"}
                {modalMode === "view" && "User Details"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {modalMode === "view" ? (
              <div className="p-6">
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <p className="text-gray-900">{selectedUser?.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <p className="text-gray-900">{selectedUser?.full_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <p className="text-gray-900">
                        {selectedUser?.phone || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <p className="text-gray-900">
                        {selectedUser?.is_system_admin
                          ? "System Administrator"
                          : "Regular User"}
                      </p>
                    </div>
                  </div>

                  {/* Tenants */}
                  {selectedUser?.tenants?.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Associated Tenants
                      </label>
                      <div className="space-y-3">
                        {selectedUser.tenants.map((tenant) => (
                          <div
                            key={tenant.id}
                            className="bg-gray-50 p-3 rounded-lg border">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-gray-900">
                                {tenant.name}
                              </h4>
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  tenant.status === "ACTIVE"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-700"
                                }`}>
                                {tenant.status}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>
                                <span className="font-medium">Slug:</span>{" "}
                                {tenant.slug}
                              </p>
                              <p>
                                <span className="font-medium">Plan:</span>{" "}
                                {tenant.subscription_plan}
                              </p>
                              {tenant.settings?.address && (
                                <p>
                                  <span className="font-medium">Address:</span>{" "}
                                  {tenant.settings.address}
                                </p>
                              )}
                              {tenant.settings?.phone && (
                                <p>
                                  <span className="font-medium">Phone:</span>{" "}
                                  {tenant.settings.phone}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Created At
                      </label>
                      <p className="text-gray-900 text-sm">
                        {new Date(selectedUser?.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Updated At
                      </label>
                      <p className="text-gray-900 text-sm">
                        {new Date(selectedUser?.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.email ? "border-red-300" : "border-gray-300"
                      }`}
                      placeholder="user@example.com"
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password{" "}
                      {modalMode === "create" && (
                        <span className="text-red-500">*</span>
                      )}
                      {modalMode === "edit" && (
                        <span className="text-gray-500">
                          (leave empty to keep current)
                        </span>
                      )}
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.password
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      placeholder="Minimum 8 characters"
                    />
                    {formErrors.password && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.password}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) =>
                        setFormData({ ...formData, full_name: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.full_name
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                      placeholder="John Doe"
                    />
                    {formErrors.full_name && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.full_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="08123456789"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.is_system_admin}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_system_admin: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        System Administrator
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      System admins can access admin panel and manage all
                      users/tenants
                    </p>
                  </div>

                  {!formData.is_system_admin && modalMode === "create" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tenant <span className="text-red-500">*</span>
                      </label>

                      {/* Tenant Debug Info */}
                      <div className="mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                        <div>Available Tenants: {tenants.length}</div>
                        <div>
                          Loading Tenants: {loadingTenants ? "Yes" : "No"}
                        </div>
                        {tenants.length === 0 && !loadingTenants && (
                          <div className="text-red-600">
                            ⚠️ No tenants available! Please load tenants first.
                          </div>
                        )}
                      </div>

                      <select
                        value={formData.tenant_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            tenant_id: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.tenant_id
                            ? "border-red-300"
                            : "border-gray-300"
                        }`}
                        disabled={loadingTenants || tenants.length === 0}>
                        <option value="">
                          {loadingTenants
                            ? "Loading tenants..."
                            : tenants.length === 0
                              ? "No tenants available"
                              : "Select a tenant"}
                        </option>
                        {tenants.map((tenant) => (
                          <option key={tenant.id} value={tenant.id}>
                            {tenant.name} ({tenant.status || "Unknown Status"})
                          </option>
                        ))}
                      </select>

                      {tenants.length === 0 && !loadingTenants && (
                        <div className="mt-2 space-x-2">
                          <button
                            type="button"
                            onClick={loadTenants}
                            className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">
                            Reload Tenants
                          </button>
                          <button
                            type="button"
                            onClick={testTenantsApi}
                            className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700">
                            Test Tenants API
                          </button>
                        </div>
                      )}

                      {formErrors.tenant_id && (
                        <p className="text-red-500 text-sm mt-1">
                          {formErrors.tenant_id}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={submitting}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={submitting}>
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        {modalMode === "create" ? "Creating..." : "Updating..."}
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        {modalMode === "create" ? "Create User" : "Update User"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
